import { useState, useEffect, useCallback } from "react";
import { api } from "@/services/api";
import { VILLA_MODELS } from "@/pages/home/villaModels";

interface Lead {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  source: string;
  page: string;
  status: string;
  stage: string;
  investment_type: string;
  revenue_model: string;
  rm_name: string;
  score: number;
  next_followup: string | null;
  created_at: string;
  activity_count: number;
  last_activity_at: string | null;
}

interface ListResponse {
  data: Lead[];
  total: number;
  page: number;
  limit: number;
}

const STATUS_OPTIONS = ["new", "contacted", "qualified", "proposal", "won", "lost"];
const STAGE_OPTIONS = ["new", "qualified", "proposal", "negotiation", "won", "lost"];
const SOURCE_OPTIONS = ["website", "whatsapp", "call", "referral", "walk-in"];
const PAGE_OPTIONS = ["contact", "investment-guide", "projects", "models"];

const STATUS_BADGE: Record<string, string> = {
  new: "bg-secondary-100 text-secondary-900",
  contacted: "bg-primary-100 text-primary-900",
  qualified: "bg-accent-100 text-accent-900",
  proposal: "bg-accent-100 text-accent-900",
  won: "bg-primary-600 text-background-50",
  lost: "bg-background-200 text-foreground-700",
};

function villaName(page: string): string {
  if (!page || !page.startsWith("villa-")) return "";
  const slug = page.slice("villa-".length);
  return VILLA_MODELS.find((v) => v.slug === slug)?.name || "";
}

export default function AdminLeads() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [origin, setOrigin] = useState("");
  const [villa, setVilla] = useState("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const originParam = villa || origin;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    if (originParam) params.set("origin", originParam);
    params.set("page", String(page));
    params.set("limit", "15");
    try {
      const res = await api.get<ListResponse>(`/leads?${params.toString()}`);
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [search, status, originParam, page]);

  useEffect(() => { load(); }, [load]);

  const pageCount = data ? Math.max(Math.ceil(data.total / data.limit), 1) : 1;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2 w-full sm:w-72">
          <i className="ri-search-line text-foreground-500"></i>
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search leads..."
            className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
          />
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700"
        >
          <i className="ri-add-line"></i> Add Lead
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">All Status</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={origin}
          onChange={(e) => { setOrigin(e.target.value); setVilla(""); setPage(1); }}
          className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">All Pages</option>
          {PAGE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          value={villa}
          onChange={(e) => { setVilla(e.target.value); setOrigin(""); setPage(1); }}
          className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-accent-400"
        >
          <option value="">All Villa Models</option>
          {VILLA_MODELS.map((v) => <option key={v.slug} value={`villa-${v.slug}`}>{v.name}</option>)}
        </select>
        {(search || status || origin || villa) && (
          <button onClick={() => { setSearch(""); setStatus(""); setOrigin(""); setVilla(""); setPage(1); }} className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-700">
            <i className="ri-close-circle-line"></i> Clear
          </button>
        )}
      </div>

      {error && <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">{error}</div>}

      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left text-xs uppercase tracking-wider text-foreground-600">
                <th className="px-4 py-3">Lead</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Page</th>
                <th className="px-4 py-3">Villa Model</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Interest</th>
                <th className="px-4 py-3">RM</th>
                <th className="px-4 py-3">Score</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {loading && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-foreground-600">
                  <i className="ri-loader-4-line animate-spin"></i> Loading...
                </td></tr>
              )}
              {!loading && data?.data.map((lead) => (
                <tr key={lead.id} className="hover:bg-background-100/60">
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground-950">{lead.full_name}</p>
                      <p className="text-xs text-foreground-600">{lead.phone || lead.email || "—"}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize text-foreground-700">{lead.source}</td>
                  <td className="px-4 py-3 capitalize text-foreground-600">{lead.page || "—"}</td>
                  <td className="px-4 py-3">
                    {villaName(lead.page) ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold px-2.5 py-1">
                        <i className="ri-vip-diamond-line text-[11px]"></i> {villaName(lead.page)}
                      </span>
                    ) : (
                      <span className="text-foreground-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${STATUS_BADGE[lead.status] || "bg-background-200 text-foreground-700"}`}>
                      {lead.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground-700 max-w-[160px] truncate">{lead.investment_type || "—"}</td>
                  <td className="px-4 py-3 text-foreground-700">{lead.rm_name || "Unassigned"}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-foreground-950">{lead.score}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setDetail(lead.id)}
                        className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100"
                        aria-label="View"
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && data?.data.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-foreground-600">No leads found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-background-100">
          <p className="text-xs text-foreground-600">Total: {data?.total ?? 0}</p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page <= 1} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-40">
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <span className="px-3 text-sm text-foreground-700">{page} / {pageCount}</span>
            <button onClick={() => setPage((p) => Math.min(p + 1, pageCount))} disabled={page >= pageCount} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-40">
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      </div>

      {detail && <LeadDetail id={detail} onClose={() => setDetail(null)} onChanged={load} />}
      {createOpen && <CreateLead onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
    </div>
  );
}

function LeadDetail({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const [lead, setLead] = useState<any>(null);
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    api.get<any>(`/leads/${id}`).then(setLead).catch(() => setLead(null));
  }, [id]);

  async function saveStatus() {
    if (!status) return;
    await api.put(`/leads/${id}`, { status });
    setStatus("");
    onChanged();
    api.get<any>(`/leads/${id}`).then(setLead);
  }

  async function addNote() {
    if (!note.trim()) return;
    await api.post(`/leads/${id}/activities`, { type: "note", note });
    setNote("");
    api.get<any>(`/leads/${id}`).then(setLead);
  }

  if (!lead) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-lg rounded-3xl bg-background-50 p-8 text-center">
          <p className="text-sm text-foreground-600">Loading lead...</p>
          <button onClick={onClose} className="mt-4 text-sm text-primary-700">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-3xl bg-background-50 border border-background-200 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100 sticky top-0 bg-background-50">
          <div>
            <h3 className="font-heading text-lg text-foreground-950">{lead.full_name}</h3>
            <p className="text-xs text-foreground-600">{lead.phone} · {lead.email}</p>
          </div>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Close">
            <i className="ri-close-line"></i>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Source" value={lead.source} />
            <Info label="Status" value={lead.status} />
            <Info label="Interest" value={lead.investment_type} />
            <Info label="Revenue Model" value={lead.revenue_model} />
            <Info label="RM" value={lead.rm_name} />
            <Info label="Score" value={String(lead.score)} />
          </div>

          <div className="rounded-2xl bg-background-100 p-4">
            <p className="text-xs font-semibold text-foreground-800 mb-2">Update Status</p>
            <div className="flex gap-2">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="flex-1 rounded-lg border border-background-300 bg-background-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                <option value="">Select status...</option>
                {STAGE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={saveStatus} className="inline-flex items-center gap-1 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-4 py-2 hover:bg-primary-700">
                Save
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-foreground-800 mb-2">Activities</p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {(lead.activities || []).map((a: any) => (
                <div key={a.id} className="rounded-xl bg-background-100 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold capitalize text-foreground-800">{a.type}</span>
                    <span className="text-[10px] text-foreground-500">{new Date(a.created_at + "Z").toLocaleString()}</span>
                  </div>
                  {a.note && <p className="text-sm text-foreground-700 mt-1">{a.note}</p>}
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a follow-up note..."
                className="flex-1 rounded-lg border border-background-300 bg-background-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <button onClick={addNote} className="inline-flex items-center gap-1 text-sm font-semibold whitespace-nowrap rounded-full bg-accent-600 text-background-50 px-4 py-2 hover:bg-accent-700">
                <i className="ri-send-plane-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-background-100 p-3">
      <p className="text-[10px] uppercase tracking-wide text-foreground-600">{label}</p>
      <p className="text-sm font-semibold text-foreground-950 capitalize">{value || "—"}</p>
    </div>
  );
}

function CreateLead({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", source: "website", investment_type: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/leads", form);
      onCreated();
    } catch (err: any) {
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-3xl bg-background-50 border border-background-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100">
          <h3 className="font-heading text-lg text-foreground-950">Add Lead</h3>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Close">
            <i className="ri-close-line"></i>
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">{error}</div>}
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Full Name *</label>
            <input required value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Phone</label>
            <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Source</label>
            <select value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
              {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-5 py-2.5 hover:bg-background-100">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700 disabled:opacity-60">
              {saving ? <><i className="ri-loader-4-line animate-spin"></i> Saving...</> : "Create Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}